import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { isPlatformApiError } from '@traceio-org/platform-client';
import { TICKET_CODE_PATTERN } from '@traceio-org/platform-contracts';
import { PageSection } from '@traceio-org/ui';
import { describeWriteFailure, type WriteFailure } from '../../api';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice, useNow } from '../../components/feedback';
import { TextField } from '../../components/fields';
import { WriteFailureMessage } from '../../components/WriteDialog';
import { useAdmin } from '../../context';
import { useAdminRead } from '../../useAdminRead';
import { NewTicketDialog } from '../tickets/NewTicketDialog';
import { TicketList } from '../tickets/TicketList';
import { useOrganizationScope } from './scope';

/** The words of a refused code, from platform-api's reason (409 code_taken, 409 code_in_use). */
function codeRefusal(error: unknown): string | null {
  if (!isPlatformApiError(error) || error.kind !== 'http') return null;
  if (error.problem.reason === 'code_in_use')
    return 'Tickets are already numbered with the current code, so it can no longer change.';
  if (error.problem.reason === 'code_taken') return 'Another organization already uses this code.';
  return null;
}

/** The ticket code: 2 to 5 capital letters (typed letters become capitals), or empty for T- numbers. */
function TicketCodeForm({
  organizationId,
  current,
  canManage,
  onSaved,
}: {
  onSaved: () => void;
  organizationId: string;
  /** The code from the organization detail (null: T- numbers). */
  current: string | null;
  canManage: boolean;
}) {
  const { api } = useAdmin();
  const [code, setCode] = useState(current ?? '');
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    const value = code.trim();
    const found =
      value && !TICKET_CODE_PATTERN.test(value) ? 'Use 2 to 5 capital letters, such as NWF.' : null;
    setError(found);
    setFailure(null);
    if (found) return;
    setSaving(true);
    try {
      const saved = await api.setOrgTicketCode(organizationId, value || null);
      onSaved();
      setNotice(
        saved.ticketCode
          ? `New tickets are numbered ${saved.ticketCode}-1, ${saved.ticketCode}-2…`
          : 'New tickets use Trace’s T- numbers.',
      );
    } catch (caught) {
      const refusal = codeRefusal(caught);
      if (refusal) setError(refusal);
      else {
        const described = describeWriteFailure(caught);
        if (described.kind !== 'saved-unreadable') setFailure(described);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <p>
        Ticket code: <strong>{current ?? 'none (T- numbers)'}</strong>
      </p>
    );
  }
  return (
    <form className="admin-inline-form" onSubmit={(event) => void save(event)} noValidate>
      <SavedNotice message={notice} />
      {failure ? <WriteFailureMessage failure={failure} /> : null}
      <TextField
        label="Ticket code"
        value={code}
        // Letters become capitals as typed; anything else stays visible so the error can say why it is refused.
        onChange={(value) => setCode(value.toUpperCase())}
        error={error}
        maxLength={5}
        autoComplete="off"
        spellCheck={false}
        hint="2 to 5 capital letters; ticket numbers read NWF-142. Fixed once a ticket is numbered with it. Leave empty for T- numbers."
      />
      <button
        type="submit"
        className="admin-action admin-action--primary"
        aria-disabled={saving || undefined}
        data-busy={saving || undefined}
      >
        Save code
      </button>
    </form>
  );
}

/**
 * An organization's tickets (/organizations/:id/tickets): every ticket of this customer in Trace's queue, a ticket
 * filed for them, and their ticket code. Staff reads only; customers' own notes never reach Admin.
 */
export function OrganizationTicketsTab() {
  const { organization, abilities, reloadOrganization } = useOrganizationScope();
  const { api } = useAdmin();
  const navigate = useNavigate();
  const now = useNow();
  const [creating, setCreating] = useState(false);
  const key = abilities.readTickets ? `org-tickets\u0000${organization.id}` : null;
  const read = useAdminRead(key, (signal) =>
    api.listTicketQueue({ view: 'all', orgId: organization.id }, signal),
  );
  const queue = read.dataKey === key && !read.failure ? read.data : null;

  let body;
  if (!abilities.readTickets) body = <p className="admin-muted">Your staff role does not include tickets.</p>;
  else if (read.failure)
    body = <AdminProblem failure={read.failure} subject="this organization's tickets" onRetry={read.retry} />;
  else if (!queue) body = <LoadingBlock label="Loading the tickets" />;
  else if (queue.items.length === 0)
    body = <p className="admin-muted">No ticket of {organization.displayName} is with Trace.</p>;
  else body = <TicketList items={queue.items} display="list" now={now} showCustomer={false} />;

  return (
    <>
      <PageSection
        title="Tickets"
        description={`${organization.displayName}'s tickets in Trace's queue.`}
        action={
          abilities.manageTickets ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => setCreating(true)}
            >
              New ticket
            </button>
          ) : undefined
        }
      >
        {body}
      </PageSection>
      <PageSection
        title="Ticket code"
        description="The letters before each ticket number. Without one, tickets use Trace's T- numbers."
      >
        <TicketCodeForm
          // One form per organization: after a save the field already holds the saved code (and keeps its notice).
          key={organization.id}
          organizationId={organization.id}
          current={organization.ticketCode ?? null}
          canManage={abilities.manageTicketCode}
          onSaved={reloadOrganization}
        />
      </PageSection>
      {abilities.manageTickets ? (
        <NewTicketDialog
          key={creating ? 'open' : 'closed'}
          open={creating}
          organizationId={organization.id}
          onClose={() => setCreating(false)}
          onCreated={(ticket) => navigate(`/tickets/${ticket.number}`)}
        />
      ) : null}
    </>
  );
}
